import React, { useMemo } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import type { PickerLocale } from 'antd/es/date-picker/generatePicker';
import type { PickerRef } from '@rc-component/picker';
import dayjs from 'dayjs';
import jaJP from 'antd/locale/ja_JP';
import { convGtJDate, convGtJMonth, convGtJYear } from '@/utils/dateConverter';

export type AppDatePickerProps = Omit<DatePickerProps, 'value'> & {
  value?: DatePickerProps['value'] | string | null;
};

const AppDatePicker = React.forwardRef<PickerRef, AppDatePickerProps>(
  ({ format, value, className, picker, ...props }, ref) => {
    // Determine the formatting logic based on the picker prop
    const isYearPicker = picker === 'year';
    const isMonthPicker = picker === 'month';

    const defaultFormat = useMemo(() => {
      if (isYearPicker) {
        return [(val: dayjs.Dayjs) => convGtJYear(val), 'YYYY'];
      }
      const conv = (val: dayjs.Dayjs) => (isMonthPicker ? convGtJMonth(val) : convGtJDate(val));
      return [conv, isMonthPicker ? 'YYYY-MM' : 'YYYY-MM-DD'];
    }, [isYearPicker, isMonthPicker]);

    const activeFormat = format || defaultFormat;

    // Convert string ISO date to Dayjs object for DatePicker
    const dayjsValue = useMemo(() => {
      if (!value) return undefined;
      if (typeof value === 'string') {
        // Try to parse ISO date string
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : undefined;
      }
      return value as dayjs.Dayjs;
    }, [value]);

    // Locale: year panel shows Japanese era (令和6年) instead of Gregorian (2024)
    // rc-picker accepts function for cellYearFormat/yearFormat at runtime; types expect string
    const pickerLocale = useMemo((): PickerLocale => {
      const base = jaJP.DatePicker!;
      return {
        ...base,
        lang: {
          ...base.lang,
          cellYearFormat: convGtJYear as (value: unknown) => string,
          yearFormat: convGtJYear as (value: unknown) => string,
        } as unknown as typeof base.lang,
      };
    }, []);

    return (
      <DatePicker
        ref={ref}
        picker={picker}
        format={activeFormat}
        value={dayjsValue}
        locale={pickerLocale}
        className={`!h-[40px] ${className ?? ''}`}
        suffixIcon={<i className="fa fa-calendar-o"></i>}
        {...props}
      />
    );
  }
);

AppDatePicker.displayName = 'AppDatePicker';

export default AppDatePicker;
